// src/contexts/CartContext.jsx
import React, { createContext, useState, useEffect } from 'react';
import { getStorageItem, setStorageItem } from '../utils/storage';

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [currentlyDisplayed, setCurrentlyDisplayed] = useState(null);
  const [currentView, setCurrentView] = useState('selection'); // 'selection' ou 'cart' ou 'delivery'
  
  // ✅ NOUVEAU : État pour le choix du client concernant les casiers
  // false = le client n'a pas de casiers (paie la consigne) - DÉFAUT
  // true = le client a des casiers vides à échanger (ne paie pas la consigne)
  const [clientHasCasiers, setClientHasCasiers] = useState(false);

  // Constante pour le montant minimal de commande
  // Modifié le 04/06/2026 : 10000 -> 15000 (demande SABC Assad du 03/06/2026)
  // TODO (échéance fin novembre 2026) : rendre ce seuil paramétrable dynamiquement
  // depuis l'interface admin — voir docs/Suivi_Projet_VBCV_PMBOK.md §11.1 (D-02)
  const MINIMUM_ORDER_AMOUNT = 15000;
  
  // ✅ NOUVEAU : Constante pour le montant de consigne par casier
  const CONSIGNE_CASIER = 3600;

  // Charger le panier depuis le stockage local au démarrage
  useEffect(() => {
    const savedCart = getStorageItem('cart');
    if (savedCart) {
      try {
        const { items, quantities, clientHasCasiers: savedClientHasCasiers } = JSON.parse(savedCart);
        if (Array.isArray(items)) setItems(items);
        if (quantities && typeof quantities === 'object') setQuantities(quantities);
        // ✅ NOUVEAU : Restaurer le choix du client
        if (typeof savedClientHasCasiers === 'boolean') setClientHasCasiers(savedClientHasCasiers);
      } catch (error) {
        // console.error('Erreur lors du chargement du panier:', error);
      }
    }
  }, []);

  // Sauvegarder le panier dans le stockage local à chaque modification
  useEffect(() => {
    // ✅ MODIFIÉ : Inclure clientHasCasiers dans la sauvegarde
    setStorageItem('cart', JSON.stringify({ items, quantities, clientHasCasiers }));
  }, [items, quantities, clientHasCasiers]);

  // ✅ MODIFIÉ : Ajouter un produit au panier avec validation de stock
  /**
   * @param {Object} product - Le produit à ajouter (doit contenir product.stock)
   * @returns {Object} - Statut { success: boolean, reason: string, stockRemaining: number }
   */
  const addToCart = (product) => {
    const currentQuantity = quantities[product.id] || 0;
    const productStock = product.stock || 0;
    
    // Vérifier si le stock maximum est atteint
    if (currentQuantity >= productStock) {
      return {
        success: false,
        reason: 'stock_max',
        stockRemaining: 0
      };
    }
    
    // Si le produit n'est pas déjà dans le panier, l'ajouter
    if (!items.find(item => item.id === product.id)) {
      setItems([...items, product]);
      setQuantities({ ...quantities, [product.id]: 1 });
    } else {
      // Sinon, augmenter la quantité
      setQuantities({
        ...quantities,
        [product.id]: (quantities[product.id] || 0) + 1
      });
    }
    
    return {
      success: true,
      stockRemaining: productStock - (currentQuantity + 1)
    };
  };

  // Retirer un produit du panier
  const removeFromCart = (productId) => {
    // Si la quantité est 1, retirer complètement le produit
    if (quantities[productId] === 1) {
      setItems(items.filter(item => item.id !== productId));
      const newQuantities = { ...quantities };
      delete newQuantities[productId];
      setQuantities(newQuantities);
    } else {
      // Sinon, diminuer la quantité
      setQuantities({
        ...quantities,
        [productId]: quantities[productId] - 1
      });
    }
  };

  // Retirer complètement un produit du panier
  const removeCompletelyFromCart = (productId) => {
    setItems(items.filter(item => item.id !== productId));
    const newQuantities = { ...quantities };
    delete newQuantities[productId];
    setQuantities(newQuantities);
  };

  // Mettre à jour la quantité d'un produit
  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeCompletelyFromCart(productId);
    } else {
      setQuantities({
        ...quantities,
        [productId]: quantity
      });
    }
  };

  // Vider le panier
  const clearCart = () => {
    setItems([]);
    setQuantities({});
    // ✅ NOUVEAU : Réinitialiser le choix des casiers
    setClientHasCasiers(false);
  };

  // Obtenir la quantité d'un produit
  const getItemQuantity = (productId) => {
    return quantities[productId] || 0;
  };

  // Calculer le total du panier (produits uniquement, sans consigne)
  const calculateTotal = () => {
    return items.reduce((total, item) => {
      return total + (item.price * quantities[item.id]);
    }, 0);
  };

  // Nombre total d'articles dans le panier
  const getTotalItems = () => {
    return Object.values(quantities).reduce((total, qty) => total + qty, 0);
  };

  // Calculer le montant restant pour atteindre le minimum
  const getRemainingAmount = () => {
    const total = calculateTotal();
    return total < MINIMUM_ORDER_AMOUNT ? MINIMUM_ORDER_AMOUNT - total : 0;
  };

  // Vérifier si le montant minimal est atteint
  const isMinimumReached = () => {
    return calculateTotal() >= MINIMUM_ORDER_AMOUNT;
  };

  // ============================================================================
  // ✅ NOUVELLES FONCTIONS POUR LA GESTION DES CONSIGNES CASIERS
  // ============================================================================

  /**
   * Vérifie si un produit est en bouteille de verre (nécessite une consigne)
   * @param {Object} item - L'article du panier
   * @returns {boolean} - true si le produit est en bouteille de verre
   */
  const isVerreProduct = (item) => {
    return item.conditionnement === 'bouteille_verre';
  };

  /**
   * Récupère tous les articles du panier qui sont en bouteille de verre
   * @returns {Array} - Liste des articles avec consigne
   */
  const getItemsWithConsigne = () => {
    return items.filter(item => isVerreProduct(item));
  };

  /**
   * Calcule le nombre total de casiers consignés dans le panier
   * Chaque pack/carton de bouteilles en verre = 1 casier
   * @returns {number} - Nombre total de casiers
   */
  const getNbCasiers = () => {
    return items.reduce((total, item) => {
      if (isVerreProduct(item)) {
        // Chaque pack commandé = 1 casier
        return total + (quantities[item.id] || 0);
      }
      return total;
    }, 0);
  };

  /**
   * Calcule le montant total de la consigne casiers
   * ✅ MODIFIÉ : Retourne 0 si le client a ses propres casiers
   * @returns {number} - Montant total de la consigne en FCFA
   */
  const calculateConsigne = () => {
    // Si le client fournit ses propres casiers, pas de consigne
    if (clientHasCasiers) {
      return 0;
    }
    const nbCasiers = getNbCasiers();
    return nbCasiers * CONSIGNE_CASIER;
  };

  /**
   * Calcule le grand total (produits + consigne)
   * C'est ce montant qui sera payé par le client
   * @returns {number} - Total à payer en FCFA
   */
  const calculateGrandTotal = () => {
    return calculateTotal() + calculateConsigne();
  };

  /**
   * Vérifie si le panier contient des produits avec consigne
   * @returns {boolean} - true si au moins un produit nécessite une consigne
   */
  const hasConsigne = () => {
    return getNbCasiers() > 0;
  };

  /**
   * Retourne les détails de la consigne pour l'affichage
   * @returns {Object} - Objet avec les détails de la consigne
   */
  const getConsigneDetails = () => {
    const nbCasiers = getNbCasiers();
    const montantConsigne = calculateConsigne();
    const itemsAvecConsigne = getItemsWithConsigne();
    
    return {
      hasConsigne: nbCasiers > 0,
      nbCasiers,
      montantConsigne,
      montantParCasier: CONSIGNE_CASIER,
      clientHasCasiers, // ✅ NOUVEAU : Inclure le choix du client
      itemsAvecConsigne: itemsAvecConsigne.map(item => ({
        id: item.id,
        nom: item.displayName || item.nom,
        quantite: quantities[item.id] || 0,
        consigneUnitaire: CONSIGNE_CASIER,
        consigneTotal: clientHasCasiers ? 0 : (quantities[item.id] || 0) * CONSIGNE_CASIER
      }))
    };
  };

  /**
   * Retourne un résumé complet du panier pour l'affichage et l'envoi API
   * @returns {Object} - Résumé complet du panier
   */
  const getCartSummary = () => {
    const totalProduits = calculateTotal();
    const consigneDetails = getConsigneDetails();
    const grandTotal = calculateGrandTotal();
    
    return {
      // Totaux
      totalProduits,
      consigneCasiers: consigneDetails.montantConsigne,
      nbCasiers: consigneDetails.nbCasiers,
      grandTotal,
      
      // Détails
      nbArticles: getTotalItems(),
      hasConsigne: consigneDetails.hasConsigne,
      clientHasCasiers, // ✅ NOUVEAU : Inclure le choix du client
      
      // Pour l'API
      montant_total: totalProduits,           // Montant produits seul
      consigne_casiers: consigneDetails.montantConsigne,  // Consigne seule (0 si client a casiers)
      nb_casiers_consignes: consigneDetails.nbCasiers,
      client_fournit_casiers: clientHasCasiers, // ✅ NOUVEAU : Pour l'API
      montant_a_payer: grandTotal             // Total à payer (produits + consigne)
    };
  };

  // ============================================================================
  // FIN DES NOUVELLES FONCTIONS
  // ============================================================================

  return (
    <CartContext.Provider
      value={{
        // États
        items,
        quantities,
        currentlyDisplayed,
        currentView,
        clientHasCasiers,        // ✅ NOUVEAU
        
        // Setters
        setCurrentlyDisplayed,
        setCurrentView,
        setClientHasCasiers,     // ✅ NOUVEAU
        
        // Actions panier
        addToCart,
        removeFromCart,
        removeCompletelyFromCart,
        updateQuantity,
        clearCart,
        getItemQuantity,
        
        // Calculs existants
        calculateTotal,
        getTotalItems,
        getRemainingAmount,
        isMinimumReached,
        
        // Constantes
        MINIMUM_ORDER_AMOUNT,
        CONSIGNE_CASIER,           // ✅ NOUVEAU
        
        // ✅ NOUVELLES FONCTIONS CONSIGNES
        isVerreProduct,
        getItemsWithConsigne,
        getNbCasiers,
        calculateConsigne,
        calculateGrandTotal,
        hasConsigne,
        getConsigneDetails,
        getCartSummary
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export default CartProvider;